import React, { useState, useEffect } from "react";
import { Modal, Form, DatePicker, Select, Button, message, Spin, Alert } from "antd";
import useExcursions from "../hooks/useExcursions";
import dayjs from "dayjs";

const { Option } = Select;

const statusOptions = [
  { value: "PENDING", label: "Pendiente" },
  { value: "IN_PROGRESS", label: "En progreso" },
  { value: "STOP_SELLING", label: "Detener ventas" },
  { value: "FINISHED", label: "Finalizada" },
];

const ScheduleExcursionFormModal = ({ visible, onCancel, editData, initialDate }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [excursionTypes, setExcursionTypes] = useState([]);
  const [fetchingTypes, setFetchingTypes] = useState(false);
  const [isFinishedExcursion, setIsFinishedExcursion] = useState(false);
  const {
    createScheduledExcursion,
    editScheduledExcursion,
    getActiveExcursionTypes,
  } = useExcursions();

  const isEditing = !!editData;

  // Fetch excursion types when modal is opened
  useEffect(() => {
    if (visible) {
      fetchExcursionTypes();
    }
  }, [visible]);

  // Set form values when editing or creating with initial date
  useEffect(() => {
    if (visible && editData) {
      // Check if excursionType exists (not a finished excursion)
      if (editData.excursionType) {
        setIsFinishedExcursion(false);
        form.setFieldsValue({
          day: editData.day ? dayjs(editData.day) : null,
          excursionType: editData.excursionType.id || editData.id,
          status: editData.status || "PENDING",
        });
      } else {
        // This is a finished excursion that wasn't returned by the API
        setIsFinishedExcursion(true);
        form.resetFields();
      }
    } else if (visible) {
      setIsFinishedExcursion(false);
      form.resetFields();
      form.setFieldsValue({
        status: "PENDING",
        day: initialDate ? dayjs(initialDate) : null,
      });
    }
  }, [visible, editData, initialDate, form]);

  const fetchExcursionTypes = async () => {
    setFetchingTypes(true);
    try {
      const response = await getActiveExcursionTypes();
      if (response?.results) {
        setExcursionTypes(response.results);
      }
    } catch (error) {
      console.error("Error fetching excursion types:", error);
      message.error("Error al cargar los tipos de excursiones");
    } finally {
      setFetchingTypes(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const formData = {
        day: values.day.format("YYYY-MM-DD"),
        excursionType: values.excursionType,
        status: values.status,
      };

      if (isEditing) {
        const response = await editScheduledExcursion({
          ...formData,
          id: editData.id,
        });
        if (response) {
          onCancel();
        }
      } else {
        const response = await createScheduledExcursion(formData);
        if (response) {
          onCancel();
        }
      }
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={isEditing ? "Editar Excursión" : "Crear Nueva Excursión"}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          disabled={isFinishedExcursion}
        >
          {isEditing ? "Actualizar" : "Crear"}
        </Button>,
      ]}
    >
      {isFinishedExcursion && (
        <Alert
          message="Excursión Finalizada"
          description={
            <div>
              Esta excursión está finalizada y no se puede modificar. Para poder editarla, debe activar la excursión de este pasajero desde la administración.<br />
              <Button
                type="link"
                style={{ paddingLeft: 0 }}
                onClick={() => {
                  if (editData?.id) {
                    window.open(`${process.env.REACT_APP_API_URL}admin/schedule_excursion/scheduledexcursion/${editData.id}/change/`, '_blank');
                  }
                }}
              >
                Ir al detalle de la excursión
              </Button>
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: "PENDING",
        }}
        disabled={isFinishedExcursion}
      >
        <Form.Item
          name="day"
          label="Fecha"
          rules={[
            { required: true, message: "Por favor seleccione una fecha" },
          ]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            placeholder="Seleccionar fecha"
          />
        </Form.Item>

        <Form.Item
          name="excursionType"
          label="Tipo de Excursión"
          rules={[
            {
              required: true,
              message: "Por favor seleccione un tipo de excursión",
            },
          ]}
        >
          <Select
            placeholder="Seleccionar tipo de excursión"
            loading={fetchingTypes}
            notFoundContent={fetchingTypes ? <Spin size="small" /> : null}
          >
            {excursionTypes.map((item) => (
              <Option key={item.id} value={item.id}>
                {item.excursionType.name} - ${item.priceAdult}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="status"
          label="Estado"
          rules={[
            { required: true, message: "Por favor seleccione un estado" },
          ]}
        >
          <Select placeholder="Seleccionar estado">
            {statusOptions.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ScheduleExcursionFormModal;
